import React, { useState, useEffect } from 'react';
import {
    Plus, Trash2, Check, X, Eye, Settings2, Edit,
    Sparkles, FileText, LayoutPanelTop, MonitorPlay, ChevronDown, RefreshCcw, Presentation,
    MoreHorizontal, Copy
} from 'lucide-react';
import CustomLoader from '../../../shared/components/ui/CustomLoader';
import CustomSelect, { SLIDE_LAYOUT_OPTIONS } from './CustomSelect';
import { markdownToHtml } from '../utils/markdownUtils';
import SlideContentEditor from './SlideEditorRegistry';

const MoreActionsDropdown = ({ activeSectionId, activeTitleId, handleRefreshSlide, handleDeleteTitle, generatingSlideId }) => {
    const [isOpen, setIsOpen] = useState(false);
    const isGenerating = generatingSlideId === activeTitleId;

    useEffect(() => {
        if (isGenerating) {
            setIsOpen(false);
        }
    }, [isGenerating]);

    return (
        <div style={{ position: 'relative' }}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                disabled={isGenerating}
                style={{
                    padding: '0.35rem 0.5rem', borderRadius: '6px', border: '1px solid #E2E8F0',
                    background: isGenerating ? '#F8F9FA' : '#fff', cursor: isGenerating ? 'not-allowed' : 'pointer',
                    display: 'flex', alignItems: 'center',
                    color: isGenerating ? '#A0AEC0' : '#475569', transition: 'background 0.2s',
                    opacity: isGenerating ? 0.6 : 1
                }}
            >
                <MoreHorizontal size={16} />
            </button>
            {isOpen && (
                <>
                    <div style={{ position: 'fixed', inset: 0, zIndex: 10 }} onClick={() => setIsOpen(false)} />
                    <div style={{
                        position: 'absolute', right: 0, top: '110%', background: '#fff',
                        border: '1px solid #E2E8F0', borderRadius: '8px', padding: '4px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 11, minWidth: '150px'
                    }}>
                        <button
                            disabled={generatingSlideId === activeTitleId}
                            onClick={() => { handleRefreshSlide(activeSectionId, activeTitleId); setIsOpen(false); }}
                            style={{
                                display: 'flex', alignItems: 'center', gap: '8px', width: '100%',
                                padding: '8px 12px', background: 'transparent', border: 'none',
                                textAlign: 'left', cursor: generatingSlideId === activeTitleId ? 'not-allowed' : 'pointer',
                                color: '#475569', borderRadius: '4px', fontSize: '13px', fontWeight: 500
                            }}
                            onMouseEnter={e => { if (generatingSlideId !== activeTitleId) e.currentTarget.style.background = '#F8F9FA'; }}
                            onMouseLeave={e => { if (generatingSlideId !== activeTitleId) e.currentTarget.style.background = 'transparent'; }}
                        >
                            <RefreshCcw size={14} /> Refresh
                        </button>
                        <button
                            onClick={() => { handleDeleteTitle(activeSectionId, activeTitleId); setIsOpen(false); }}
                            style={{
                                display: 'flex', alignItems: 'center', gap: '8px', width: '100%',
                                padding: '8px 12px', background: 'transparent', border: 'none',
                                textAlign: 'left', cursor: 'pointer', color: '#EF4444', borderRadius: '4px', fontSize: '13px', fontWeight: 500
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = '#FEF2F2'}
                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                        >
                            <Trash2 size={14} /> Delete
                        </button>
                    </div>
                </>
            )}
        </div>
    );
};



const PPTSlideEditor = ({
    isCreatingNewSlide,
    isCreatingNewSection,
    activeTitle,
    activeSection,
    activeSectionId,
    activeTitleId,
    sections,
    newSlideForm,
    setNewSlideForm,
    newSectionForm,
    setNewSectionForm,
    isSaving,
    handleConfirmCreateSlide,
    setIsCreatingNewSlide,
    handleSaveNewSection,
    setIsCreatingNewSection,
    handleFieldChange,
    handleUpdateSlide,
    handleDeleteTitle,
    handleGenerateSlide,
    handleRefreshSlide,
    handleAddTitle,
    generatingSlideId,
    previewImageUrl,
    setPreviewImageUrl,
    isPreviewLoading,
    handlePreviewSlide,
    showPreview,
    creatingInSectionId,
    handleSaveSlideContent,
    originalSlides
}) => {
    const [showAIStrategy, setShowAIStrategy] = useState(false);

    const original = originalSlides?.[activeTitleId];

    return (
        <section className="ppt-main-editor">
            {isCreatingNewSlide ? (
                <div className="editor-card animate-fade-in">
                    <div className="editor-card-header">
                        <div className="editor-header-left">
                            <span className="breadcrumb-small">ADDING SLIDE TO: {sections.find(s => s.id === creatingInSectionId)?.name}</span>
                            <input
                                className="h2-inline-edit"
                                placeholder="Enter Slide Title (e.g., EXECUTIVE SUMMARY)"
                                value={newSlideForm.title}
                                onChange={(e) => setNewSlideForm({ ...newSlideForm, title: e.target.value })}
                                autoFocus
                            />
                        </div>
                    </div>
                    <div className="editor-body">
                        <div className="editor-content-split">
                            <div className="ai-strategy-section" style={{ borderTop: 'none', paddingTop: 0 }}>
                                <div className="field-header">
                                    <div className="field-label-group">
                                        <Sparkles size={16} className="sparkle-icon" />
                                        <label>AI Generation Strategy</label>
                                    </div>
                                </div>
                                <div className="strategy-controls-row" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                    <div className="format-selection-group">
                                        <span className="format-label">Format:</span>
                                        <CustomSelect
                                            value={newSlideForm.content_type === 'bullet' ? 'bullets' : (newSlideForm.content_type || 'bullets')}
                                            onChange={(val) => setNewSlideForm({ ...newSlideForm, content_type: val })}
                                            options={SLIDE_LAYOUT_OPTIONS}
                                        />
                                    </div>
                                    <div className="prompt-container" style={{ position: 'relative' }}>
                                        <textarea
                                            className="prompt-modern-textarea"
                                            placeholder="Describe how AI should generate this slide..."
                                            value={newSlideForm.system_prompt || ''}
                                            onChange={(e) => setNewSlideForm({ ...newSlideForm, system_prompt: e.target.value })}
                                            style={{ height: '120px', minHeight: '120px' }}
                                        />
                                        {(!newSlideForm.system_prompt || !newSlideForm.system_prompt.trim()) && (
                                            <div className="prompt-validation-warning" style={{ color: '#E9471D', fontSize: '12px', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '6px', marginTop: '6px', padding: '6px 10px', background: '#FFF1EC', border: '1px solid #FFE1D6', borderRadius: '6px' }}>
                                                <span>⚠️ Please give the prompt for this</span>
                                            </div>
                                        )}
                                        <div className="prompt-actions-inline" style={{ marginTop: '12px' }}>
                                            <button className="btn-secondary-outline-sm" onClick={() => setIsCreatingNewSlide(false)} disabled={isSaving}>Cancel</button>
                                            <button
                                                className={`btn-primary-gradient-sm ${isSaving ? 'loading' : ''}`}
                                                onClick={handleConfirmCreateSlide}
                                                disabled={isSaving || !newSlideForm.system_prompt || !newSlideForm.system_prompt.trim()}
                                                style={{
                                                    opacity: (isSaving || !newSlideForm.system_prompt || !newSlideForm.system_prompt.trim()) ? 0.6 : 1,
                                                    cursor: (isSaving || !newSlideForm.system_prompt || !newSlideForm.system_prompt.trim()) ? 'not-allowed' : 'pointer'
                                                }}
                                            >
                                                {isSaving ? <CustomLoader size={14} color="#FFFFFF" /> : <Plus size={14} />}
                                                <span>{isSaving ? 'Creating...' : 'Create Slide'}</span>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ) : isCreatingNewSection ? (
                <div className="editor-card animate-fade-in">
                    <div className="editor-card-header">
                        <div className="editor-header-left">
                            <span className="breadcrumb-small">NEW SECTION DEFINITION</span>
                            <input
                                className="h2-inline-edit"
                                placeholder="Enter Section Name (e.g., EXECUTIVE SUMMARY)"
                                value={newSectionForm.section_name}
                                onChange={(e) => setNewSectionForm({ ...newSectionForm, section_name: e.target.value })}
                                autoFocus
                            />
                        </div>
                    </div>
                    <div className="editor-body" style={{ padding: '1.5rem', background: '#FFFFFF', borderTop: '1px solid #F1F5F9' }}>
                        <div className="prompt-actions-inline">
                            <button className="btn-secondary-outline-sm" onClick={() => setIsCreatingNewSection(false)} disabled={isSaving}>Cancel</button>
                            <button className={`btn-primary-gradient-sm ${isSaving ? 'loading' : ''}`} onClick={handleSaveNewSection} disabled={isSaving || !newSectionForm.section_name.trim()}>
                                {isSaving ? <CustomLoader size={14} color="#FFFFFF" /> : <Plus size={14} />}
                                <span>{isSaving ? 'Creating...' : 'Create Section'}</span>
                            </button>
                        </div>
                    </div>
                </div>
            ) : activeTitle ? (
                <div className="editor-card animate-fade-in" style={(activeTitle.type === 'static' || previewImageUrl) ? { background: '#FFFFFF', border: '1px solid #E5E7EB', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)' } : {}}>
                    <div className="editor-card-header" style={(activeTitle.type === 'static' || previewImageUrl) ? { borderBottom: '1px solid #F1F5F9', padding: '1rem 1.25rem' } : {}}>
                        <div className="editor-header-left">
                            {activeTitle.isEditable ? (
                                <input
                                    className="h2-inline-edit"
                                    value={activeTitle.text}
                                    onChange={(e) => handleFieldChange(activeSectionId, activeTitleId, 'text', e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') { e.target.blur(); handleUpdateSlide(activeSectionId, activeTitleId); } }}
                                    onBlur={() => { if (original && activeTitle.text !== original.text) handleUpdateSlide(activeSectionId, activeTitleId); }}
                                    placeholder="Slide Title"
                                />
                            ) : (
                                <h2 className="h2-inline-edit-static" title={activeTitle.text}>{activeTitle.text}</h2>
                            )}
                        </div>
                        <div className="editor-header-actions" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            {activeTitle.isEditable && activeTitle.type !== 'static' && (
                                <button
                                    className={`btn-secondary-outline ${isPreviewLoading ? 'loading' : ''}`}
                                    onClick={() => previewImageUrl ? setPreviewImageUrl(null) : handlePreviewSlide(activeSectionId, activeTitleId, true)}
                                    disabled={isPreviewLoading || generatingSlideId === activeTitleId}
                                    style={{
                                        padding: '0.35rem 0.75rem',
                                        fontSize: '0.75rem',
                                        borderRadius: '6px',
                                        height: 'auto',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.4rem',
                                        border: '1px solid #E2E8F0',
                                        background: (generatingSlideId === activeTitleId) ? '#F8F9FA' : (previewImageUrl ? '#F1F5F9' : 'white'),
                                        cursor: (isPreviewLoading || generatingSlideId === activeTitleId) ? 'not-allowed' : 'pointer',
                                        opacity: (generatingSlideId === activeTitleId) ? 0.6 : 1
                                    }}
                                >
                                    {isPreviewLoading ? <CustomLoader size={14} color="#475569" /> : (previewImageUrl ? <Edit size={14} /> : <Eye size={14} />)}
                                    <span style={{ fontWeight: 600, color: '#475569' }}>{previewImageUrl ? 'Edit Content' : 'View Slide'}</span>
                                </button>
                            )}
                            <MoreActionsDropdown
                                activeSectionId={activeSectionId}
                                activeTitleId={activeTitleId}
                                handleRefreshSlide={handleRefreshSlide}
                                handleDeleteTitle={handleDeleteTitle}
                                generatingSlideId={generatingSlideId}
                            />
                        </div>
                    </div>

                    <div className="editor-body" style={(activeTitle.type === 'static' || previewImageUrl) ? { padding: '1.5rem', background: '#F8F9FA', overflow: 'auto' } : {}}>
                        {activeTitle.type === 'static' ? (
                            <div className="preview-image-container animate-fade-in" style={{ display: 'flex', flexDirection: (previewImageUrl && activeTitle?.overflowPreviews?.length) ? 'column' : 'row', justifyContent: (previewImageUrl && activeTitle?.overflowPreviews?.length) ? 'flex-start' : 'center', alignItems: 'center', gap: '1.5rem', height: '100%', width: '100%', padding: '1rem', boxSizing: 'border-box', overflowY: 'auto', background: 'transparent', flex: 1 }}>
                                {isPreviewLoading ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}><CustomLoader size={32} /><p style={{ color: '#64748B', fontSize: '0.85rem' }}>Loading static preview...</p></div>
                                ) : previewImageUrl ? (
                                    <>
                                        <img src={previewImageUrl} alt="Static Slide Preview" style={{ maxWidth: '100%', maxHeight: (activeTitle?.overflowPreviews?.length) ? 'none' : 'calc(100vh - 220px)', width: 'auto', height: 'auto', objectFit: 'contain', border: '1px solid rgba(0,0,0,0.08)', borderRadius: '8px', boxShadow: '0 12px 36px rgba(0,0,0,0.12), 0 4px 12px rgba(0,0,0,0.06)', background: 'white' }} />
                                        {(activeTitle?.overflowPreviews || []).map((url, i) => (
                                            <img key={i} src={url} alt={`Static Slide Overflow ${i + 1}`} style={{ maxWidth: '100%', height: 'auto', objectFit: 'contain', border: '1px solid rgba(0,0,0,0.08)', borderRadius: '8px', boxShadow: '0 12px 36px rgba(0,0,0,0.12), 0 4px 12px rgba(0,0,0,0.06)', background: 'white' }} />
                                        ))}
                                    </>
                                ) : (
                                    <div className="empty-editor-selection animate-fade-in">
                                        <div className="empty-state-card">
                                            <div className="empty-state-icon-wrapper">
                                                <Eye size={32} />
                                            </div>
                                            <span className="empty-state-pill">Template Slide</span>
                                            <h2>Static Preview Slide</h2>
                                            <p>This slide is part of the static template. Select an AI slide to edit content.</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <>
                                {!activeTitle.isEditable && (
                                    <div className="lock-notice" style={{ margin: '1rem 1.25rem 0' }}>
                                        <Settings2 size={14} />
                                        <span>This content is part of a static template and cannot be edited.</span>
                                    </div>
                                )}
                                {previewImageUrl ? (
                                    <div className="preview-image-container animate-fade-in" style={{ display: 'flex', flexDirection: (previewImageUrl && activeTitle?.overflowPreviews?.length) ? 'column' : 'row', justifyContent: (previewImageUrl && activeTitle?.overflowPreviews?.length) ? 'flex-start' : 'center', alignItems: 'center', gap: '1.5rem', height: '100%', width: '100%', padding: '1rem', boxSizing: 'border-box', overflowY: 'auto', background: 'transparent', flex: 1 }}>
                                        <img src={previewImageUrl} alt="Slide Preview" style={{ maxWidth: '100%', maxHeight: (activeTitle?.overflowPreviews?.length) ? 'none' : 'calc(100vh - 220px)', width: 'auto', height: 'auto', objectFit: 'contain', border: '1px solid rgba(0,0,0,0.08)', borderRadius: '8px', boxShadow: '0 12px 36px rgba(0,0,0,0.12), 0 4px 12px rgba(0,0,0,0.06)', background: 'white' }} />
                                        {(activeTitle?.overflowPreviews || []).map((url, i) => (
                                            <img key={i} src={url} alt={`Slide Overflow ${i + 1}`} style={{ maxWidth: '100%', height: 'auto', objectFit: 'contain', border: '1px solid rgba(0,0,0,0.08)', borderRadius: '8px', boxShadow: '0 12px 36px rgba(0,0,0,0.12), 0 4px 12px rgba(0,0,0,0.06)', background: 'white' }} />
                                        ))}
                                    </div>
                                ) : (
                                    <div className="editor-content-split">
                                        <div className="content-view-section" style={{
                                            padding: '0 0 16px 0',
                                            filter: (generatingSlideId === activeTitleId) ? 'blur(2px)' : 'none',
                                            pointerEvents: (generatingSlideId === activeTitleId) ? 'none' : 'auto',
                                            transition: 'all 0.3s ease',
                                            opacity: (generatingSlideId === activeTitleId) ? 0.6 : 1
                                        }}>
                                            {/* We can remove the old "Presentation Details" header entirely since SlideContentEditor now has a top bar */}

                                            <div style={{ marginTop: 12 }}>
                                                <SlideContentEditor
                                                    slide={{
                                                        ...activeTitle,
                                                        slide_id: activeTitle.id,
                                                        archetype: activeTitle.archetype || activeTitle.layout,
                                                        content: activeTitle.rawContent ?? activeTitle.content,
                                                        column_headers: activeTitle.column_headers,
                                                        is_static: activeTitle.is_static ?? activeTitle.type === 'static',
                                                    }}
                                                    onSave={(structuredContent) => handleSaveSlideContent(activeSectionId, activeTitleId, structuredContent)}
                                                    isSaving={isSaving}
                                                />
                                            </div>
                                        </div>

                                        <div className={`ai-strategy-section ${showAIStrategy ? 'active' : ''}`}>
                                            <div className="field-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }} onClick={() => setShowAIStrategy(!showAIStrategy)}>
                                                <div className="field-label-group">
                                                    <Sparkles size={16} className="sparkle-icon" />
                                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                        <label style={{ cursor: 'pointer' }}>AI Generation Strategy</label>
                                                        <span className="field-helper" style={{ margin: 0 }}>Define how AI should generate this slide</span>
                                                    </div>
                                                </div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }} onClick={(e) => e.stopPropagation()}>
                                                    <label className="switch-toggle">
                                                        <input type="checkbox" checked={showAIStrategy} onChange={() => setShowAIStrategy(!showAIStrategy)} />
                                                        <span className="slider-round"></span>
                                                    </label>
                                                    <ChevronDown size={16} className={`chevron-toggle-icon ${showAIStrategy ? 'rotated' : ''}`} onClick={() => setShowAIStrategy(!showAIStrategy)} />
                                                </div>
                                            </div>
                                            {showAIStrategy && (
                                                <div className="strategy-controls-row" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                                    <div className="format-selection-group">
                                                        <span className="format-label">FORMAT:</span>
                                                        <CustomSelect
                                                            value={activeTitle.layout === 'bullet' ? 'bullets' : (activeTitle.layout || 'bullets')}
                                                            onChange={(val) => handleFieldChange(activeSectionId, activeTitleId, 'layout', val)}
                                                            options={SLIDE_LAYOUT_OPTIONS}
                                                            disabled={!activeTitle.isEditable}
                                                        />
                                                    </div>
                                                    <div className="prompt-container" style={{ position: 'relative' }}>
                                                        <textarea
                                                            className="prompt-modern-textarea"
                                                            value={activeTitle.prompt || ''}
                                                            onChange={(e) => handleFieldChange(activeSectionId, activeTitleId, 'prompt', e.target.value)}
                                                            placeholder={activeTitle.isEditable ? "e.g., Focus on technical scalability..." : "Strategy editing disabled for this slide type."}
                                                            disabled={!activeTitle.isEditable}
                                                        />
                                                        <span className="prompt-helper-text">Provide guidance for AI to generate better, more relevant content. (Optional)</span>
                                                        <div className="prompt-actions">
                                                            <button
                                                                className={`btn-generate-slide ${generatingSlideId === activeTitleId ? 'generating' : ''}`}
                                                                onClick={() => handleGenerateSlide(activeSectionId, activeTitleId)}
                                                                disabled={!activeTitle.isEditable || generatingSlideId === activeTitleId}
                                                                style={{
                                                                    opacity: (!activeTitle.isEditable || generatingSlideId === activeTitleId) ? 0.6 : 1,
                                                                    cursor: (!activeTitle.isEditable || generatingSlideId === activeTitleId) ? 'not-allowed' : 'pointer'
                                                                }}
                                                            >
                                                                {generatingSlideId === activeTitleId ? (
                                                                    <><CustomLoader size={14} color="#FFFFFF" /><span>Generating...</span></>
                                                                ) : (
                                                                    <><Sparkles size={16} /><span>{activeTitle.isEditable ? 'Generate Slide' : 'Generation Locked'}</span></>
                                                                )}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            ) : activeSection ? (
                <div className="empty-editor-selection animate-fade-in">
                    <div className="empty-state-card">
                        <div className="empty-state-icon-wrapper">
                            <LayoutPanelTop size={32} />
                        </div>
                        <span className="empty-state-pill">Section Selected</span>
                        <h2>{activeSection.name}</h2>
                        <p style={{ marginBottom: '1.5rem' }}>Select a slide from the sidebar to edit its content or add a new slide to this section.</p>
                        <button className="btn-primary-gradient-sm" onClick={() => handleAddTitle(activeSectionId)} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                            <Plus size={16} />
                            <span>Add Slide</span>
                        </button>
                    </div>
                </div>
            ) : (
                <div className="empty-editor-selection">
                    <div className="empty-state-card">
                        <div className="empty-state-icon-wrapper" style={{ background: '#F1F5F9', color: '#64748B' }}>
                            <LayoutPanelTop size={32} />
                        </div>
                        <h2>No Section Selected</h2>
                        <p>Select a section and slide from the document outline to begin customization.</p>
                    </div>
                </div>
            )}

            {showPreview && (
                <aside className="ppt-right-panel animate-slide-in-right">
                    <div className="panel-preview-content">
                        <div className="panel-header">
                            <MonitorPlay size={18} />
                            <h4>Live Preview</h4>
                        </div>
                        <div className="preview-placeholder">
                            <div className="mock-slide">
                                <div className="mock-title">{activeTitle?.text}</div>
                                <div className="mock-body"><p>{activeTitle?.content}</p></div>
                            </div>
                            <p className="preview-hint">This is a high-level representation of the generated slide.</p>
                        </div>
                    </div>
                </aside>
            )}
        </section>
    );
};

export default PPTSlideEditor;
